/**
 * Flaticon UIcons (regular rounded) for the browser panels — the same icon pack
 * ROP-teams uses (Bill 2026-11-04: one icon pack across every PCB app).
 *
 * Each export keeps the lucide-react name it replaces and accepts the props the
 * panels passed to lucide (size, className, style, color, aria-hidden), so a
 * screen switches packs by changing only its import line. They render
 * `<i class="fi fi-rr-{slug}">`; each app imports
 * '@flaticon/flaticon-uicons/css/regular/rounded.css' once in main.tsx (bundled
 * from npm — no CDN). Decorative by default (aria-hidden) unless aria-label is set.
 *
 * Slugs: https://www.flaticon.com/uicons (lookup only; the font ships in the bundle).
 */

import type { CSSProperties } from 'react';

export interface FiIconProps {
  /** Pixel size (font-size). Defaults to 24, lucide's default. */
  size?: number | string;
  className?: string;
  style?: CSSProperties;
  color?: string;
  'aria-hidden'?: boolean | 'true' | 'false';
  'aria-label'?: string;
  /** Accepted for lucide compatibility; a font icon has no stroke width. */
  strokeWidth?: number;
}

export function FiIcon({
  slug,
  size = 24,
  className,
  style,
  color,
  'aria-label': ariaLabel,
}: FiIconProps & { slug: string }): JSX.Element {
  const s: CSSProperties = {
    fontSize: size,
    lineHeight: 1,
    // inline-block so a transform (the .spin animation) applies outside flex rows too.
    display: 'inline-block',
    ...(color !== undefined ? { color } : null),
    ...style,
  };
  const cls = `fi fi-rr-${slug}${className ? ` ${className}` : ''}`;
  return ariaLabel ? (
    <i className={cls} style={s} role="img" aria-label={ariaLabel} />
  ) : (
    <i className={cls} style={s} aria-hidden="true" />
  );
}

function fi(slug: string, name: string): (p: FiIconProps) => JSX.Element {
  const C = (p: FiIconProps): JSX.Element => <FiIcon slug={slug} {...p} />;
  (C as { displayName?: string }).displayName = name;
  return C;
}

/** lucide name → Flaticon slug, for tests and for looking a replacement up. */
export const FI_SLUGS = {
  Activity: 'pulse',
  AlertCircle: 'exclamation',
  AlertOctagon: 'octagon-exclamation',
  AlertTriangle: 'triangle-warning',
  Archive: 'archive',
  ArrowDownRight: 'down-right',
  ArrowLeft: 'arrow-left',
  ArrowRight: 'arrow-right',
  ArrowUpRight: 'arrow-up-right',
  BadgeCheck: 'badge-check',
  Ban: 'ban',
  BarChart3: 'chart-histogram',
  Bell: 'bell',
  BellRing: 'bell-ring',
  Bold: 'bold',
  BookCheck: 'book-bookmark',
  BookOpen: 'book-open-cover',
  Bug: 'bug',
  Building2: 'building',
  CalendarCheck: 'calendar-check',
  CalendarClock: 'calendar-clock',
  CalendarDays: 'calendar-days',
  CalendarPlus: 'calendar-plus',
  CheckCircle: 'check-circle',
  CheckCircle2: 'check-circle',
  CheckSquare: 'checkbox',
  ChevronDown: 'angle-small-down',
  ChevronLeft: 'angle-small-left',
  ChevronRight: 'angle-small-right',
  ChevronUp: 'angle-small-up',
  ChevronsUpDown: 'sort-alt',
  Circle: 'circle',
  CircleAlert: 'exclamation',
  CircleCheck: 'check-circle',
  CircleHelp: 'interrogation',
  Clipboard: 'clipboard',
  ClipboardCheck: 'clipboard-check',
  ClipboardList: 'clipboard-list',
  ClipboardType: 'paste',
  Clock: 'clock',
  CloudRainWind: 'cloud-showers-heavy',
  CloudUpload: 'cloud-upload-alt',
  Code: 'code-simple',
  Columns2: 'table-columns',
  Compass: 'compass-alt',
  Copy: 'copy',
  CreditCard: 'credit-card',
  Database: 'database',
  DollarSign: 'dollar',
  Download: 'download',
  Edit: 'edit',
  Edit3: 'edit',
  ExternalLink: 'arrow-up-right-from-square',
  Eye: 'eye',
  EyeOff: 'eye-crossed',
  File: 'file',
  FileArchive: 'file-zipper',
  FileCheck2: 'document-signed',
  FilePen: 'file-edit',
  FilePlus: 'add-document',
  FileSearch: 'search-alt',
  FileSignature: 'file-signature',
  FileStack: 'copy',
  FileText: 'document',
  Files: 'copy-alt',
  FlaskConical: 'flask',
  FolderOpen: 'folder-open',
  Gauge: 'tachometer-fast',
  GitBranch: 'code-branch',
  Globe: 'globe',
  Globe2: 'globe',
  Hash: 'hastag',
  Heart: 'heart',
  HeartPulse: 'heart-rate',
  HelpCircle: 'interrogation',
  History: 'time-past',
  Home: 'home',
  IdCard: 'id-badge',
  Image: 'picture',
  Inbox: 'inbox',
  Info: 'info',
  Italic: 'italic',
  KeyRound: 'key',
  Layers: 'layers',
  LayoutGrid: 'apps',
  LifeBuoy: 'life-ring',
  LineChart: 'chart-line-up',
  Link: 'link',
  Link2: 'link',
  List: 'list',
  ListChecks: 'list-check',
  ListOrdered: 'sort-numeric-down',
  Loader2: 'spinner',
  Lock: 'lock',
  Mail: 'envelope',
  MailCheck: 'envelope-open',
  MapPin: 'marker',
  MessageSquare: 'comment',
  MessageSquarePlus: 'comment-medical',
  Minus: 'minus',
  Monitor: 'computer',
  MonitorSmartphone: 'devices',
  MoreHorizontal: 'menu-dots',
  MoreVertical: 'menu-dots-vertical',
  Package: 'box',
  PauseCircle: 'pause-circle',
  PenLine: 'pen-field',
  Pencil: 'pencil',
  Phone: 'phone-call',
  PhoneCall: 'phone-call',
  PiggyBank: 'piggy-bank',
  Play: 'play',
  PlayCircle: 'play-circle',
  Plus: 'plus',
  PowerOff: 'power',
  Printer: 'print',
  Redo2: 'redo',
  RefreshCw: 'refresh',
  Replace: 'replace',
  RotateCcw: 'rotate-left',
  Save: 'disk',
  ScrollText: 'scroll-old',
  Search: 'search',
  Send: 'paper-plane',
  Settings: 'settings',
  Settings2: 'settings-sliders',
  Shield: 'shield',
  ShieldAlert: 'shield-exclamation',
  ShieldCheck: 'shield-check',
  ShoppingCart: 'shopping-cart',
  Shuffle: 'shuffle',
  Skull: 'skull',
  Smartphone: 'mobile-notch',
  StickyNote: 'note-sticky',
  Table: 'table',
  TestTube2: 'test-tube',
  Timer: 'stopwatch',
  Trash2: 'trash',
  Underline: 'underline',
  Undo2: 'undo',
  Unlink: 'link-slash',
  Upload: 'upload',
  User: 'user',
  UserCheck: 'user-check',
  UserCog: 'user-gear',
  UserPlus: 'user-add',
  UserRoundCog: 'user-gear',
  UserX: 'user-xmark',
  Users: 'users',
  Variable: 'brackets-curly',
  Video: 'video-camera',
  Wallet: 'wallet',
  Wifi: 'wifi',
  Workflow: 'workflow',
  Wrench: 'wrench-simple',
  X: 'cross-small',
  XCircle: 'cross-circle',
  ZoomIn: 'zoom-in',
  ZoomOut: 'zoom-out',
} as const;

/** A Flaticon icon component; LucideIcon is the name screens already use for it. */
export type FiIconComponent = (p: FiIconProps) => JSX.Element;
export type LucideIcon = FiIconComponent;

export const Activity: FiIconComponent = fi(FI_SLUGS.Activity, 'Activity');
export const AlertCircle: FiIconComponent = fi(FI_SLUGS.AlertCircle, 'AlertCircle');
export const AlertOctagon: FiIconComponent = fi(FI_SLUGS.AlertOctagon, 'AlertOctagon');
export const AlertTriangle: FiIconComponent = fi(FI_SLUGS.AlertTriangle, 'AlertTriangle');
export const Archive: FiIconComponent = fi(FI_SLUGS.Archive, 'Archive');
export const ArrowDownRight: FiIconComponent = fi(FI_SLUGS.ArrowDownRight, 'ArrowDownRight');
export const ArrowLeft: FiIconComponent = fi(FI_SLUGS.ArrowLeft, 'ArrowLeft');
export const ArrowRight: FiIconComponent = fi(FI_SLUGS.ArrowRight, 'ArrowRight');
export const ArrowUpRight: FiIconComponent = fi(FI_SLUGS.ArrowUpRight, 'ArrowUpRight');
export const BadgeCheck: FiIconComponent = fi(FI_SLUGS.BadgeCheck, 'BadgeCheck');
export const Ban: FiIconComponent = fi(FI_SLUGS.Ban, 'Ban');
export const BarChart3: FiIconComponent = fi(FI_SLUGS.BarChart3, 'BarChart3');
export const Bell: FiIconComponent = fi(FI_SLUGS.Bell, 'Bell');
export const BellRing: FiIconComponent = fi(FI_SLUGS.BellRing, 'BellRing');
export const Bold: FiIconComponent = fi(FI_SLUGS.Bold, 'Bold');
export const BookCheck: FiIconComponent = fi(FI_SLUGS.BookCheck, 'BookCheck');
export const BookOpen: FiIconComponent = fi(FI_SLUGS.BookOpen, 'BookOpen');
export const Bug: FiIconComponent = fi(FI_SLUGS.Bug, 'Bug');
export const Building2: FiIconComponent = fi(FI_SLUGS.Building2, 'Building2');
export const CalendarCheck: FiIconComponent = fi(FI_SLUGS.CalendarCheck, 'CalendarCheck');
export const CalendarClock: FiIconComponent = fi(FI_SLUGS.CalendarClock, 'CalendarClock');
export const CalendarDays: FiIconComponent = fi(FI_SLUGS.CalendarDays, 'CalendarDays');
export const CalendarPlus: FiIconComponent = fi(FI_SLUGS.CalendarPlus, 'CalendarPlus');
export const CheckCircle: FiIconComponent = fi(FI_SLUGS.CheckCircle, 'CheckCircle');
export const CheckCircle2: FiIconComponent = fi(FI_SLUGS.CheckCircle2, 'CheckCircle2');
export const CheckSquare: FiIconComponent = fi(FI_SLUGS.CheckSquare, 'CheckSquare');
export const ChevronDown: FiIconComponent = fi(FI_SLUGS.ChevronDown, 'ChevronDown');
export const ChevronLeft: FiIconComponent = fi(FI_SLUGS.ChevronLeft, 'ChevronLeft');
export const ChevronRight: FiIconComponent = fi(FI_SLUGS.ChevronRight, 'ChevronRight');
export const ChevronUp: FiIconComponent = fi(FI_SLUGS.ChevronUp, 'ChevronUp');
export const ChevronsUpDown: FiIconComponent = fi(FI_SLUGS.ChevronsUpDown, 'ChevronsUpDown');
export const Circle: FiIconComponent = fi(FI_SLUGS.Circle, 'Circle');
export const CircleAlert: FiIconComponent = fi(FI_SLUGS.CircleAlert, 'CircleAlert');
export const CircleCheck: FiIconComponent = fi(FI_SLUGS.CircleCheck, 'CircleCheck');
export const CircleHelp: FiIconComponent = fi(FI_SLUGS.CircleHelp, 'CircleHelp');
export const Clipboard: FiIconComponent = fi(FI_SLUGS.Clipboard, 'Clipboard');
export const ClipboardCheck: FiIconComponent = fi(FI_SLUGS.ClipboardCheck, 'ClipboardCheck');
export const ClipboardList: FiIconComponent = fi(FI_SLUGS.ClipboardList, 'ClipboardList');
export const ClipboardType: FiIconComponent = fi(FI_SLUGS.ClipboardType, 'ClipboardType');
export const Clock: FiIconComponent = fi(FI_SLUGS.Clock, 'Clock');
export const CloudRainWind: FiIconComponent = fi(FI_SLUGS.CloudRainWind, 'CloudRainWind');
export const CloudUpload: FiIconComponent = fi(FI_SLUGS.CloudUpload, 'CloudUpload');
export const Code: FiIconComponent = fi(FI_SLUGS.Code, 'Code');
export const Columns2: FiIconComponent = fi(FI_SLUGS.Columns2, 'Columns2');
export const Compass: FiIconComponent = fi(FI_SLUGS.Compass, 'Compass');
export const Copy: FiIconComponent = fi(FI_SLUGS.Copy, 'Copy');
export const CreditCard: FiIconComponent = fi(FI_SLUGS.CreditCard, 'CreditCard');
export const Database: FiIconComponent = fi(FI_SLUGS.Database, 'Database');
export const DollarSign: FiIconComponent = fi(FI_SLUGS.DollarSign, 'DollarSign');
export const Download: FiIconComponent = fi(FI_SLUGS.Download, 'Download');
export const Edit: FiIconComponent = fi(FI_SLUGS.Edit, 'Edit');
export const Edit3: FiIconComponent = fi(FI_SLUGS.Edit3, 'Edit3');
export const ExternalLink: FiIconComponent = fi(FI_SLUGS.ExternalLink, 'ExternalLink');
export const Eye: FiIconComponent = fi(FI_SLUGS.Eye, 'Eye');
export const EyeOff: FiIconComponent = fi(FI_SLUGS.EyeOff, 'EyeOff');
export const File: FiIconComponent = fi(FI_SLUGS.File, 'File');
export const FileArchive: FiIconComponent = fi(FI_SLUGS.FileArchive, 'FileArchive');
export const FileCheck2: FiIconComponent = fi(FI_SLUGS.FileCheck2, 'FileCheck2');
export const FilePen: FiIconComponent = fi(FI_SLUGS.FilePen, 'FilePen');
export const FilePlus: FiIconComponent = fi(FI_SLUGS.FilePlus, 'FilePlus');
export const FileSearch: FiIconComponent = fi(FI_SLUGS.FileSearch, 'FileSearch');
export const FileSignature: FiIconComponent = fi(FI_SLUGS.FileSignature, 'FileSignature');
export const FileStack: FiIconComponent = fi(FI_SLUGS.FileStack, 'FileStack');
export const FileText: FiIconComponent = fi(FI_SLUGS.FileText, 'FileText');
export const Files: FiIconComponent = fi(FI_SLUGS.Files, 'Files');
export const FlaskConical: FiIconComponent = fi(FI_SLUGS.FlaskConical, 'FlaskConical');
export const FolderOpen: FiIconComponent = fi(FI_SLUGS.FolderOpen, 'FolderOpen');
export const Gauge: FiIconComponent = fi(FI_SLUGS.Gauge, 'Gauge');
export const GitBranch: FiIconComponent = fi(FI_SLUGS.GitBranch, 'GitBranch');
export const Globe: FiIconComponent = fi(FI_SLUGS.Globe, 'Globe');
export const Globe2: FiIconComponent = fi(FI_SLUGS.Globe2, 'Globe2');
export const Hash: FiIconComponent = fi(FI_SLUGS.Hash, 'Hash');
export const Heart: FiIconComponent = fi(FI_SLUGS.Heart, 'Heart');
export const HeartPulse: FiIconComponent = fi(FI_SLUGS.HeartPulse, 'HeartPulse');
export const HelpCircle: FiIconComponent = fi(FI_SLUGS.HelpCircle, 'HelpCircle');
export const History: FiIconComponent = fi(FI_SLUGS.History, 'History');
export const Home: FiIconComponent = fi(FI_SLUGS.Home, 'Home');
export const IdCard: FiIconComponent = fi(FI_SLUGS.IdCard, 'IdCard');
export const Image: FiIconComponent = fi(FI_SLUGS.Image, 'Image');
export const Inbox: FiIconComponent = fi(FI_SLUGS.Inbox, 'Inbox');
export const Info: FiIconComponent = fi(FI_SLUGS.Info, 'Info');
export const Italic: FiIconComponent = fi(FI_SLUGS.Italic, 'Italic');
export const KeyRound: FiIconComponent = fi(FI_SLUGS.KeyRound, 'KeyRound');
export const Layers: FiIconComponent = fi(FI_SLUGS.Layers, 'Layers');
export const LayoutGrid: FiIconComponent = fi(FI_SLUGS.LayoutGrid, 'LayoutGrid');
export const LifeBuoy: FiIconComponent = fi(FI_SLUGS.LifeBuoy, 'LifeBuoy');
export const LineChart: FiIconComponent = fi(FI_SLUGS.LineChart, 'LineChart');
export const Link: FiIconComponent = fi(FI_SLUGS.Link, 'Link');
export const Link2: FiIconComponent = fi(FI_SLUGS.Link2, 'Link2');
export const List: FiIconComponent = fi(FI_SLUGS.List, 'List');
export const ListChecks: FiIconComponent = fi(FI_SLUGS.ListChecks, 'ListChecks');
export const ListOrdered: FiIconComponent = fi(FI_SLUGS.ListOrdered, 'ListOrdered');
export const Loader2: FiIconComponent = fi(FI_SLUGS.Loader2, 'Loader2');
export const Lock: FiIconComponent = fi(FI_SLUGS.Lock, 'Lock');
export const Mail: FiIconComponent = fi(FI_SLUGS.Mail, 'Mail');
export const MailCheck: FiIconComponent = fi(FI_SLUGS.MailCheck, 'MailCheck');
export const MapPin: FiIconComponent = fi(FI_SLUGS.MapPin, 'MapPin');
export const MessageSquare: FiIconComponent = fi(FI_SLUGS.MessageSquare, 'MessageSquare');
export const MessageSquarePlus: FiIconComponent = fi(FI_SLUGS.MessageSquarePlus, 'MessageSquarePlus');
export const Minus: FiIconComponent = fi(FI_SLUGS.Minus, 'Minus');
export const Monitor: FiIconComponent = fi(FI_SLUGS.Monitor, 'Monitor');
export const MonitorSmartphone: FiIconComponent = fi(FI_SLUGS.MonitorSmartphone, 'MonitorSmartphone');
export const MoreHorizontal: FiIconComponent = fi(FI_SLUGS.MoreHorizontal, 'MoreHorizontal');
export const MoreVertical: FiIconComponent = fi(FI_SLUGS.MoreVertical, 'MoreVertical');
export const Package: FiIconComponent = fi(FI_SLUGS.Package, 'Package');
export const PauseCircle: FiIconComponent = fi(FI_SLUGS.PauseCircle, 'PauseCircle');
export const PenLine: FiIconComponent = fi(FI_SLUGS.PenLine, 'PenLine');
export const Pencil: FiIconComponent = fi(FI_SLUGS.Pencil, 'Pencil');
export const Phone: FiIconComponent = fi(FI_SLUGS.Phone, 'Phone');
export const PhoneCall: FiIconComponent = fi(FI_SLUGS.PhoneCall, 'PhoneCall');
export const PiggyBank: FiIconComponent = fi(FI_SLUGS.PiggyBank, 'PiggyBank');
export const Play: FiIconComponent = fi(FI_SLUGS.Play, 'Play');
export const PlayCircle: FiIconComponent = fi(FI_SLUGS.PlayCircle, 'PlayCircle');
export const Plus: FiIconComponent = fi(FI_SLUGS.Plus, 'Plus');
export const PowerOff: FiIconComponent = fi(FI_SLUGS.PowerOff, 'PowerOff');
export const Printer: FiIconComponent = fi(FI_SLUGS.Printer, 'Printer');
export const Redo2: FiIconComponent = fi(FI_SLUGS.Redo2, 'Redo2');
export const RefreshCw: FiIconComponent = fi(FI_SLUGS.RefreshCw, 'RefreshCw');
export const Replace: FiIconComponent = fi(FI_SLUGS.Replace, 'Replace');
export const RotateCcw: FiIconComponent = fi(FI_SLUGS.RotateCcw, 'RotateCcw');
export const Save: FiIconComponent = fi(FI_SLUGS.Save, 'Save');
export const ScrollText: FiIconComponent = fi(FI_SLUGS.ScrollText, 'ScrollText');
export const Search: FiIconComponent = fi(FI_SLUGS.Search, 'Search');
export const Send: FiIconComponent = fi(FI_SLUGS.Send, 'Send');
export const Settings: FiIconComponent = fi(FI_SLUGS.Settings, 'Settings');
export const Settings2: FiIconComponent = fi(FI_SLUGS.Settings2, 'Settings2');
export const Shield: FiIconComponent = fi(FI_SLUGS.Shield, 'Shield');
export const ShieldAlert: FiIconComponent = fi(FI_SLUGS.ShieldAlert, 'ShieldAlert');
export const ShieldCheck: FiIconComponent = fi(FI_SLUGS.ShieldCheck, 'ShieldCheck');
export const ShoppingCart: FiIconComponent = fi(FI_SLUGS.ShoppingCart, 'ShoppingCart');
export const Shuffle: FiIconComponent = fi(FI_SLUGS.Shuffle, 'Shuffle');
export const Skull: FiIconComponent = fi(FI_SLUGS.Skull, 'Skull');
export const Smartphone: FiIconComponent = fi(FI_SLUGS.Smartphone, 'Smartphone');
export const StickyNote: FiIconComponent = fi(FI_SLUGS.StickyNote, 'StickyNote');
export const Table: FiIconComponent = fi(FI_SLUGS.Table, 'Table');
export const TestTube2: FiIconComponent = fi(FI_SLUGS.TestTube2, 'TestTube2');
export const Timer: FiIconComponent = fi(FI_SLUGS.Timer, 'Timer');
export const Trash2: FiIconComponent = fi(FI_SLUGS.Trash2, 'Trash2');
export const Underline: FiIconComponent = fi(FI_SLUGS.Underline, 'Underline');
export const Undo2: FiIconComponent = fi(FI_SLUGS.Undo2, 'Undo2');
export const Unlink: FiIconComponent = fi(FI_SLUGS.Unlink, 'Unlink');
export const Upload: FiIconComponent = fi(FI_SLUGS.Upload, 'Upload');
export const User: FiIconComponent = fi(FI_SLUGS.User, 'User');
export const UserCheck: FiIconComponent = fi(FI_SLUGS.UserCheck, 'UserCheck');
export const UserCog: FiIconComponent = fi(FI_SLUGS.UserCog, 'UserCog');
export const UserPlus: FiIconComponent = fi(FI_SLUGS.UserPlus, 'UserPlus');
export const UserRoundCog: FiIconComponent = fi(FI_SLUGS.UserRoundCog, 'UserRoundCog');
export const UserX: FiIconComponent = fi(FI_SLUGS.UserX, 'UserX');
export const Users: FiIconComponent = fi(FI_SLUGS.Users, 'Users');
export const Variable: FiIconComponent = fi(FI_SLUGS.Variable, 'Variable');
export const Video: FiIconComponent = fi(FI_SLUGS.Video, 'Video');
export const Wallet: FiIconComponent = fi(FI_SLUGS.Wallet, 'Wallet');
export const Wifi: FiIconComponent = fi(FI_SLUGS.Wifi, 'Wifi');
export const Workflow: FiIconComponent = fi(FI_SLUGS.Workflow, 'Workflow');
export const Wrench: FiIconComponent = fi(FI_SLUGS.Wrench, 'Wrench');
export const X: FiIconComponent = fi(FI_SLUGS.X, 'X');
export const XCircle: FiIconComponent = fi(FI_SLUGS.XCircle, 'XCircle');
export const ZoomIn: FiIconComponent = fi(FI_SLUGS.ZoomIn, 'ZoomIn');
export const ZoomOut: FiIconComponent = fi(FI_SLUGS.ZoomOut, 'ZoomOut');
